import React from "react";

import { faTrashAlt } from "@fortawesome/pro-regular-svg-icons";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import { FieldErrors } from "src/api/types";
import { AutocompleteCodeInput } from "src/base-components/CodeInput/EditorCodeInput";
import { FormItem } from "src/base-components/FormItem";
import { Pill } from "src/base-components/Pill";
import { Select } from "src/base-components/Select";
import { Switch } from "src/base-components/Switch";
import {
  CustomConnectionForm,
  FileExtractionEncoding,
  FileExtractionOperation,
} from "src/customConnectionNode/types";
import { IconButton } from "src/design-system/Icon";
import { FEATURE_FLAGS, isFeatureFlagEnabled } from "src/router/featureFlags";

const operationOptions: {
  key: FileExtractionOperation;
  value: string;
  description: string;
}[] = [
  {
    key: "attribute_extraction",
    value: "Field in response JSON",
    description: "Extract a value from a JSON path in the response body",
  },
  {
    key: "content",
    value: "Binary content",
    description: "Treat the whole response body as the file content",
  },
];

const encodingOptions: {
  key: FileExtractionEncoding;
  value: string;
  description: string;
}[] = [
  {
    key: "base64",
    value: "base64",
    description: "Decode base64-encoded content",
  },
  {
    key: "gzip",
    value: "gzip",
    description: "Decompress gzip-encoded content",
  },
];

// File extensions are easier for users to reason about than MIME types; the BE
// stores the resolved content type, so the form value is mapped both ways.
const FILE_TYPE_TO_CONTENT_TYPE: Record<string, string> = {
  ".txt": "text/plain",
  ".pdf": "application/pdf",
  ".png": "image/png",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".webp": "image/webp",
  ".csv": "text/csv",
  ".json": "application/json",
  ".html": "text/html",
};

const fileTypeOptions = Object.keys(FILE_TYPE_TO_CONTENT_TYPE).map((ext) => ({
  key: ext,
  value: ext,
}));

const FILE_KEY_DESCRIPTION = (
  <>
    Unique identifier for this file in the{" "}
    <Pill size="sm" variant="gray">
      <Pill.Text>files</Pill.Text>
    </Pill>{" "}
    object
  </>
);

const RESPONSE_PATH_DESCRIPTION = (
  <>
    JSON path where the file content is located, e.g.{" "}
    <Pill size="sm" variant="gray">
      <Pill.Text>body.data.report</Pill.Text>
    </Pill>
  </>
);

const OUTPUT_LOCATION_DESCRIPTION = (
  <>
    Path under{" "}
    <Pill size="sm" variant="gray">
      <Pill.Text>data.</Pill.Text>
    </Pill>{" "}
    where the file id is written
  </>
);

// `.jpg` and `.jpeg` both map to `image/jpeg`; reversing the entries makes
// `.jpg` the canonical display value (it appears later in source order, so
// `Object.fromEntries` keeps it as the last write).
const CONTENT_TYPE_TO_FILE_TYPE: Record<string, string> = Object.fromEntries(
  Object.entries(FILE_TYPE_TO_CONTENT_TYPE).map(([ext, mime]) => [mime, ext]),
);

const ToggleRow: React.FC<{
  label: string;
  description: React.ReactNode;
  enabled: boolean;
  disabled: boolean;
  dataLoc: string;
  onChange: () => void;
}> = ({ label, description, enabled, disabled, dataLoc, onChange }) => (
  <div className="flex items-start justify-between gap-4">
    <div className="flex-1">
      <div className="font-inter-semibold-13px text-gray-800">{label}</div>
      <p className="mt-1 font-inter-normal-12px text-gray-500">{description}</p>
    </div>
    <Switch
      dataLoc={dataLoc}
      disabled={disabled}
      enabled={enabled}
      onChange={onChange}
    />
  </div>
);

type Props = {
  index: number;
  onRemove: () => void;
  immutable: boolean;
  runFieldErrors: FieldErrors | undefined;
};

export const FileExtractionRow: React.FC<Props> = ({
  index,
  onRemove,
  immutable,
  runFieldErrors,
}) => {
  const { control, getValues } = useFormContext<CustomConnectionForm>();
  const operation = useWatch({
    control,
    name: `config.file_extraction_config.extractions.${index}.operation`,
  });
  const fieldPrefix =
    `config.file_extraction_config.extractions.${index}` as const;
  const fileKeyFieldError =
    runFieldErrors?.[getValues(`${fieldPrefix}.file_key.id`) ?? ""];
  const pathFieldError =
    runFieldErrors?.[getValues(`${fieldPrefix}.path.id`) ?? ""];
  const defaultContentTypeFieldError =
    runFieldErrors?.[getValues(`${fieldPrefix}.default_content_type.id`) ?? ""];
  const outputFieldError =
    runFieldErrors?.[getValues(`${fieldPrefix}.output.id`) ?? ""];
  const dataLocPrefix = `cc-node-file-extraction-${index}`;
  const showDataStrippingToggle = isFeatureFlagEnabled(
    FEATURE_FLAGS.showCcFilesDataStripping,
  );

  return (
    <div
      className="mb-4 flex flex-col gap-y-4 rounded-lg bg-gray-50 px-5 py-4"
      data-loc={`${dataLocPrefix}-row`}
    >
      <FormItem
        label="File key"
        description={FILE_KEY_DESCRIPTION}
        gap={false}
        isRequired
      >
        <div className="flex items-center gap-x-2">
          <div className="flex-1">
            <Controller
              control={control}
              name={`${fieldPrefix}.file_key.value`}
              render={({ field }) => (
                <AutocompleteCodeInput
                  dataLoc={`${dataLocPrefix}-file-key`}
                  disabled={immutable}
                  error={fileKeyFieldError}
                  placeholder="credit_report"
                  value={field.value ?? ""}
                  plaintext
                  onChange={field.onChange}
                />
              )}
            />
          </div>
          {!immutable && (
            <IconButton
              dataLoc={`${dataLocPrefix}-remove`}
              icon={faTrashAlt}
              size="sm"
              onClick={onRemove}
            />
          )}
        </div>
      </FormItem>

      <FormItem
        label="Extract file from"
        description="Where the file lives in the response"
        gap={false}
      >
        <Controller
          control={control}
          name={`${fieldPrefix}.operation`}
          render={({ field }) => (
            <Select
              dataLoc={`${dataLocPrefix}-operation`}
              disabled={immutable}
              options={operationOptions}
              placeholder="Select operation"
              value={field.value}
              onChange={(value) =>
                field.onChange(value as FileExtractionOperation)
              }
            />
          )}
        />
      </FormItem>

      {operation === "attribute_extraction" && (
        <>
          <FormItem
            label="Response path"
            description={RESPONSE_PATH_DESCRIPTION}
            helpTooltipBody="Use dot notation to walk into nested fields of the JSON response body."
            gap={false}
            isRequired
          >
            <Controller
              control={control}
              name={`${fieldPrefix}.path.value`}
              render={({ field }) => (
                <AutocompleteCodeInput
                  dataLoc={`${dataLocPrefix}-path`}
                  disabled={immutable}
                  error={pathFieldError}
                  placeholder="body.data.report"
                  value={field.value ?? ""}
                  plaintext
                  onChange={field.onChange}
                />
              )}
            />
          </FormItem>
          <FormItem
            label="Content encoding"
            description="Decoding steps applied to the extracted value before storing the file, applied in the order shown"
            gap={false}
          >
            <Controller
              control={control}
              name={`${fieldPrefix}.encodings`}
              render={({ field }) => (
                <Select
                  dataLoc={`${dataLocPrefix}-encodings`}
                  disabled={immutable}
                  multiple
                  options={encodingOptions}
                  placeholder="No encoding"
                  value={field.value ?? []}
                  onChange={(value) =>
                    field.onChange(value as FileExtractionEncoding[])
                  }
                />
              )}
            />
          </FormItem>
        </>
      )}

      <FormItem label="File type" gap={false} isRequired>
        <Controller
          control={control}
          name={`${fieldPrefix}.default_content_type.value`}
          render={({ field }) => (
            <Select
              dataLoc={`${dataLocPrefix}-file-type`}
              disabled={immutable}
              errored={Boolean(defaultContentTypeFieldError)}
              errorTooltip={defaultContentTypeFieldError}
              options={fileTypeOptions}
              placeholder="Select file type"
              value={CONTENT_TYPE_TO_FILE_TYPE[field.value ?? ""] ?? ""}
              onChange={(value) =>
                field.onChange(FILE_TYPE_TO_CONTENT_TYPE[value] ?? "")
              }
            />
          )}
        />
      </FormItem>

      <FormItem
        label="Output location"
        description={OUTPUT_LOCATION_DESCRIPTION}
        gap={false}
      >
        <Controller
          control={control}
          name={`${fieldPrefix}.output.mapped_to`}
          render={({ field }) => (
            <AutocompleteCodeInput
              dataLoc={`${dataLocPrefix}-output`}
              disabled={immutable}
              error={outputFieldError}
              prefix="data."
              value={field.value ?? ""}
              onChange={field.onChange}
            />
          )}
        />
      </FormItem>

      <Controller
        control={control}
        name={`${fieldPrefix}.required`}
        render={({ field }) => (
          <ToggleRow
            dataLoc={`${dataLocPrefix}-required`}
            description="When enabled, the node fails if this file cannot be extracted from the response. When disabled, missing files are skipped."
            disabled={immutable}
            enabled={!!field.value}
            label="Require file to proceed"
            onChange={() => field.onChange(!field.value)}
          />
        )}
      />

      {showDataStrippingToggle && (
        <Controller
          control={control}
          name={`${fieldPrefix}.strip_file_data_from_response`}
          render={({ field }) => (
            <ToggleRow
              dataLoc={`${dataLocPrefix}-strip-file-data-from-response`}
              description="When enabled, the extracted file data is removed from the response body before it is returned to downstream nodes. Useful to avoid carrying large file payloads alongside the resolved file id."
              disabled={immutable}
              enabled={!!field.value}
              label="Strip file data from response"
              onChange={() => field.onChange(!field.value)}
            />
          )}
        />
      )}
    </div>
  );
};
